import { useMemo, useState, useEffect, useRef, memo } from "react";
import { Minus, Plus } from "lucide-react";
import Footer from "../Footer";
import { useTranslation } from "react-i18next";
import { updateMetaTags, seoData } from "@/utils/seo";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import { motion } from "framer-motion";
import { landingMotionInView } from "@/utils/landingMotion";
import { AnimatePresence } from "framer-motion";
import CommunityCard from "../landing/CommunityCard";

interface FaqItem {
  id: number;
  question: string;
  answer: string;
}

interface FaqAccordionRowProps {
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}

const FaqAccordionRow = memo(({ faq, isOpen, onToggle }: FaqAccordionRowProps) => {
  return (
    <motion.div
      layout
      className={
        isOpen
          ? "faq-gradient !shadow-none"
          : "rounded-[16px] md:rounded-[22px] border border-white/20 bg-[rgba(255,255,255,0.02)]"
      }
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <div className={`relative overflow-hidden rounded-[21px]`}>
        {isOpen ? (
          <div
            className="pointer-events-none absolute -left-[20%] -top-1/2 h-[140%] w-[70%] "
            aria-hidden
          />
        ) : null}

        <button
          type="button"
          onClick={onToggle}
          className="relative z-10 flex w-full items-start justify-between gap-4 px-6 py-4 text-left sm:px-6"
        >
          <span className="font-creato text-xl font-bold text-white md:text-2xl">
            {faq.question}
          </span>
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white">
            <AnimatePresence mode="wait" initial={false}>
              {isOpen ? (
                <motion.span
                  key="minus"
                  initial={{ opacity: 0, scale: 0.85, rotate: -90 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.85, rotate: 90 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex"
                  aria-hidden
                >
                  <Minus size={28} className="h-6 w-6 stroke-[2.5]" />
                </motion.span>
              ) : (
                <motion.span
                  key="plus"
                  initial={{ opacity: 0, scale: 0.85, rotate: 90 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.85, rotate: -90 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex"
                  aria-hidden
                >
                  <Plus className="h-6 w-6 stroke-[2.5]" />
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </button>

        <AnimatePresence initial={false}>
          {isOpen ? (
            <motion.div
              key={`answer-${faq.id}`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.42, ease: [0.22, 1, 0.36, 1] },
                opacity: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
              }}
              className="relative z-10 overflow-hidden"
            >
              <div className="px-6 min-[400px]:pr-16 xl:!pr-32 pb-6 pt-0 font-nexa text-base leading-relaxed text-white sm:px-6 sm:pb-6 md:text-lg">
                {faq.answer}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});

export default function Faqs() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const faqCategory = useMemo(
    () => [
      { category: t("All FAQs") },
      { category: t("General") },
      { category: t("Getting Started") },
      { category: t("Platform") },
      { category: t("Rules") },
    ],
    [t]
  );

  const faqData = useMemo(
    () => [
      {
        id: 1,
        question: t("What is URFX?"),
        answer: t(
          "URFX is a proprietary trading firm that empowers skilled traders by providing them with the capital and resources needed to trade effectively. We offer various funding options and support to help traders succeed in the financial markets."
        ),
        category: "General",
      },
      {
        id: 2,
        question: t("How do I become a funded trader with URFX?"),
        answer: t(
          "To become a funded trader with URFX, you need to successfully complete our evaluation process, which assesses your trading skills and risk management abilities. Upon passing, you'll receive a funded account to trade with."
        ),
        category: "Getting Started",
      },
      {
        id: 3,
        question: t("What are the account sizes available?"),
        answer: t(
          "We offer multiple account sizes to cater to different trading preferences. Specific details about each account size and their respective requirements can be found on our Programs page."
        ),
        category: "General",
      },
      {
        id: 4,
        question: t("Is there a fee to participate in the evaluation?"),
        answer: t(
          "Yes, there is a one-time fee to participate in our evaluation process. This fee covers the administrative costs of assessing your trading performance and is fully refundable upon achieving specific milestones in your funded account."
        ),
        category: "Rules",
      },
      {
        id: 5,
        question: t("What platforms can I use to trade?"),
        answer: t(
          "URFX supports trading on popular platforms such as MetaTrader 4 (MT4) and MetaTrader 5 (MT5), available on desktop, web, and mobile versions for your convenience."
        ),
        category: "Platform",
      },
      {
        id: 6,
        question: t(
          "Can I use Expert Advisors (EAs) or automated trading strategies?"
        ),
        answer: t(
          "Yes, the use of Expert Advisors (EAs) and automated trading strategies is permitted, provided they adhere to our trading rules and do not exploit platform inefficiencies."
        ),
        category: "Rules",
      },
      {
        id: 7,
        question: t("What are the profit-sharing arrangements?"),
        answer: t(
          "Our traders receive a competitive profit split, allowing them to retain a significant portion of the profits they generate. Detailed information on profit-sharing ratios is available on our Programs page."
        ),
        category: "General",
      },
      {
        id: 8,
        question: t("How are payouts processed?"),
        answer: t(
          "Payouts are processed upon request. Traders can request their share of profits through our dashboard, and payments are typically processed within a specified timeframe. More details can be found in our Payout Policy."
        ),
        category: "Getting Started",
      },
      {
        id: 9,
        question: t("Are there any trading restrictions?"),
        answer: t(
          "Traders are expected to adhere to our risk management rules, including maximum daily loss limits and overall drawdown limits. Specific details are outlined in our Trading Rules section."
        ),
        category: "Rules",
      },
      {
        id: 10,
        question: t("Can I hold trades overnight or over the weekend?"),
        answer: t(
          "Yes, traders are allowed to hold positions overnight and over weekends. However, it's essential to be aware of potential market gaps and increased volatility during these periods."
        ),
        category: "Rules",
      },
      {
        id: 11,
        question: t("Is news trading allowed?"),
        answer: t(
          "Yes, trading during news events is permitted. Traders should exercise caution during high-impact news releases due to potential market volatility."
        ),
        category: "Rules",
      },
      {
        id: 12,
        question: t("How is the daily loss limit calculated?"),
        answer: t(
          "The daily loss limit is calculated based on a percentage of your account balance or equity. Detailed calculations and examples are provided in our Risk Management Guidelines."
        ),
        category: "Rules",
      },
      {
        id: 13,
        question: t("What happens if I violate a trading rule?"),
        answer: t(
          "Violating trading rules may result in the termination of your funded account. It's crucial to familiarize yourself with all trading rules to maintain your account status."
        ),
        category: "Rules",
      },
      {
        id: 14,
        question: t("Can I trade multiple accounts simultaneously?"),
        answer: t(
          "Yes, traders can manage multiple accounts, provided they comply with our account management policies. Specific guidelines are available in our Multiple Accounts Policy."
        ),
        category: "Platform",
      },
      {
        id: 15,
        question: t("Are there any restrictions on trading instruments?"),
        answer: t(
          "Traders have access to a wide range of instruments, including forex pairs, commodities, indices, and cryptocurrencies. A comprehensive list is available on our Trading Instruments page."
        ),
        category: "Rules",
      },
      {
        id: 16,
        question: t("How do I request a payout?"),
        answer: t(
          "Payouts can be requested through the trader dashboard. Detailed instructions are provided in our Payout Request Guide."
        ),
        category: "General",
      },
      {
        id: 17,
        question: t("How can I track my trading performance?"),
        answer: t(
          "Our trader dashboard provides comprehensive analytics and performance metrics to help you monitor and improve your trading strategies."
        ),
        category: "General",
      },
      {
        id: 18,
        question: t("Is there a minimum trading day requirement?"),
        answer: t(
          "Yes, traders are required to trade a minimum number of days during the evaluation phase. Specific details are outlined in our Evaluation Guidelines."
        ),
        category: "Rules",
      },
      {
        id: 19,
        question: t("How do I get started with URFX?"),
        answer: t(
          "To get started, visit our Sign-Up Page, choose your preferred evaluation program, and follow the registration process. Our support team is available to assist you throughout your journey with URFX."
        ),
        category: "Getting Started",
      },
    ],
    [t]
  );

  const [selectedCategory, setSelectedCategory] = useState<string>("All FAQs");
  const [activeId, setActiveId] = useState<number | null>(null);
  const faqRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Function to get the first FAQ item for the selected category
  const getFirstFaqForCategory = (category: string) => {
    if (category === "All FAQs") {
      return faqData[0]; // Return the first FAQ item overall
    }
    return faqData.find(faq => faq.category === category) || null;
  };

  // Effect to set the first FAQ as active when category changes
  useEffect(() => {
    // Update SEO meta tags for FAQ page
    updateMetaTags(seoData.faq);
  }, []);

  useEffect(() => {
    const firstFaq = getFirstFaqForCategory(selectedCategory);
    if (firstFaq) {
      setActiveId(firstFaq.id);
    }
  }, [selectedCategory]);

  const filteredFaqs = useMemo(() => {
    if (selectedCategory === "All FAQs") return faqData;
    return faqData.filter((f) => f.category === selectedCategory);
  }, [faqData, selectedCategory]);

  /** Matches TradingRules / ExtendedBanner shell */
  const GLASS_CARD =
    "relative backdrop-blur-sm rounded-[20px] border border-white/35 bg-gradient-to-b from-white/0 to-white/10";

  return (
    <div
      className="h-full w-full bg-dark text-white  overflow-hidden relative"
      id="faq"
    >
        <img src="/image/herosection/ellipse.png" alt="ellipse" className="absolute h-[1000px] w-[500px] top-[10%] z-20 left-0 object-cover" />

      <Navbar />
      

      {/* Header Background */}
      <video
        className="absolute top-0 -mt-5 z-10 inset-0 h-[160vh] w-full object-cover hidden md:block "
        autoPlay
        loop
        muted
        playsInline
      >
        <source src="/assets/HeroBackground.mp4" type="video/mp4" />
      </video>

      <video
        className="absolute top-0 -mt-5 z-10 h-[900px] w-full object-cover block md:hidden"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src="/assets/phonevideo.mp4" type="video/mp4" />
      </video>

      <div className="relative z-10 pt-14 pb-32 md:py-14 bg-[url('/image/faqs/coinsmob.png')] lg:bg-[url('/image/faqs/coins.png')] bg-contain md:bg-auto bg-center bg-no-repeat mx-auto max-w-[1240px] px-4 xl:px-0">

        {/* hero information */}
        {/* subtitle The lading prop firm */}
        <div className="flex flex-col items-center justify-center  pt-4">
          <motion.div
            className="w-fit h-auto p-[1.5px] rounded-[20px] bg-gradient-to-l from-[#1CCDE6] to-[#DBD633] flex justify-center items-center"
            {...landingMotionInView}
          >
            <div className="w-full px-3 bg-black/80 backdrop-blur-sm text-dark rounded-[20px] py-1 flex items-center justify-center">
              <span
                className="bg-clip-text font-creato text-sm md:text-base uppercase text-transparent font-bold"
                style={{
                  backgroundImage:
                    "linear-gradient(to left, #D7D536, #20CDE2)",
                  display: "inline-block",
                }}
              >
                {t("Rules & Regulations")}
              </span>
            </div>
          </motion.div>
        </div>
        <motion.h1
          className="text-[32px] mx-auto max-w-xl px-4 xl:px-0 text-center md:text-[56px] leading-tight font-bold pt-5 pb-2"
          {...landingMotionInView}
        >
          <span className="italic text-gradient font-normal px-1 ">
            {t("Frequently")}
          </span>
          <br /> {t("Asked Questions")}
        </motion.h1>
        <motion.p
          className="text-base  text-white text-center md:text-xl max-w-[550px] mx-auto font-normal pt-0 "
          {...landingMotionInView}
        >
          {t(
            "Welcome to the URFX FAQ section. Here, we’ve compiled answers to the most common questions about our platform, trading rules, and account management."
          )}
        </motion.p>


      </div>

      <div className="pt-5 lg:pt-16 pb-20  max-w-7xl mx-auto xl:px-0 px-4 relative z-30">
        <div className="mx-auto md:mt-10 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-[390px_1fr] gap-8 items-start">
            {/* Left sticky categories (desktop) */}
            <aside className=" lg:sticky lg:top-10 z-30">
              <div className={`${GLASS_CARD}`}>
                <div className="max-h-[70vh] overflow-auto">
                  {faqCategory.map((c) => {
                    const isActive = selectedCategory === c.category;
                    return (
                      <button
                        key={c.category}
                        type="button"
                        onClick={() => setSelectedCategory(c.category)}
                        className={`w-full text-left p-4 transition-colors border-b border-white/5 last:border-b-0 ${
                          isActive ? "text-gradient from-5%" : "hover:bg-white/5"
                        }`}
                        aria-current={isActive ? "true" : undefined}
                      >
                        <div className="text-base font-nexa font-medium tracking-wider">
                          {c.category}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>

            {/* Right column (mobile categories + accordion) */}
            <div className="space-y-6 lg:space-y-0">
              

              {/* Accordion list */}
              <motion.div
                className="overflow-hidden backdrop-blur-none space-y-4"
                layout
              >
                <AnimatePresence mode="wait">
                  {filteredFaqs.map((faq) => (
                    <motion.div
                      key={faq.id}
                      ref={(el) => {
                        faqRefs.current[faq.id] = el;
                      }}
                      id={`faq-${faq.id}`}
                      className="scroll-mt-28"
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaqAccordionRow
                        faq={faq}
                        isOpen={activeId === faq.id}
                        onToggle={() => {
                          setActiveId(activeId === faq.id ? null : faq.id);
                        }}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* CTA */}
                <motion.div layout className="pt-6 flex justify-center">
                  <button
                    onClick={() => navigate("/support")}
                    className="btn-gradient-left font-nexa w-fit min-w-[160px] rounded-[10px] px-6 py-2.5 text-lg font-bold"

                  >
                    {t("Got More Questions?")}
                  </button>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      

      <CommunityCard />
      <Footer />
    </div>
  );
}